import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Avatar,
  Fade,
  Chip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PageHeader from '../components/common/PageHeader';
import { UserService } from '../services/user.service';
import { Role } from '../types/auth.types';

const schema = yup.object({
  username: yup.string().required('Имя пользователя обязательно'),
  password: yup.string()
    .min(8, 'Пароль должен содержать минимум 8 символов')
    .required('Пароль обязателен'),
  email: yup.string()
    .email('Введите корректный email')
    .required('Email обязателен'),
  fullName: yup.string().required('Полное имя обязательно'),
  roleIds: yup.array().min(1, 'Выберите хотя бы одну роль'),
}).required();

interface UserFormData {
  username: string;
  password: string;
  email: string;
  fullName: string;
  roleIds: string[];
}

export default function UserCreate() {
  const navigate = useNavigate();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  const { control, handleSubmit, formState: { errors } } = useForm<UserFormData>({
    resolver: yupResolver(schema) as Resolver<UserFormData>,
    defaultValues: {
      username: '',
      password: '',
      email: '',
      fullName: '',
      roleIds: [],
    }
  });

  useEffect(() => {
    setMounted(true);
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const rolesData = await UserService.getAllRoles();
      setRoles(rolesData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки ролей');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      setSubmitting(true);
      setError(null);

      await UserService.createUser(data);

      setSuccess('Пользователь успешно создан');
      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка создания пользователя');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <Box>
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <PersonAddIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Создание пользователя
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Добавление нового пользователя в систему
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {error && (
        <Fade in timeout={400}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      )}

      {success && (
        <Fade in timeout={400}>
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            {success}
          </Alert>
        </Fade>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <CircularProgress size={60} sx={{ color: '#667eea' }} />
        </Box>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fade in={mounted} timeout={600}>
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <Box sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', p: 2.5, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Личная информация
                  </Typography>
                </Box>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="username"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Имя пользователя"
                          fullWidth
                          required
                          error={!!errors.username}
                          helperText={errors.username?.message}
                          disabled={submitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': { borderColor: '#4facfe' },
                              '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="fullName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Полное имя"
                          fullWidth
                          required
                          error={!!errors.fullName}
                          helperText={errors.fullName?.message}
                          disabled={submitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': { borderColor: '#4facfe' },
                              '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email"
                          type="email"
                          fullWidth
                          required
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          disabled={submitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': { borderColor: '#4facfe' },
                              '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Пароль"
                          type="password"
                          fullWidth
                          required
                          error={!!errors.password}
                          helperText={errors.password?.message}
                          disabled={submitting}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': { borderColor: '#4facfe' },
                              '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Fade>

          <Fade in={mounted} timeout={800}>
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <Box sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', p: 2.5, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SecurityIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Роли пользователя
                  </Typography>
                </Box>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Controller
                  name="roleIds"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <FormGroup>
                        {roles.map((role) => (
                          <Box
                            key={role.id}
                            sx={{
                              p: 2,
                              mb: 1,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: field.value.includes(role.id) ? '#f5576c' : 'divider',
                              bgcolor: field.value.includes(role.id) ? 'rgba(245, 87, 108, 0.05)' : 'transparent',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: '#f5576c',
                                bgcolor: 'rgba(245, 87, 108, 0.05)',
                              }
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value.includes(role.id)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    const newRoleIds = checked
                                      ? [...field.value, role.id]
                                      : field.value.filter(id => id !== role.id);
                                    field.onChange(newRoleIds);
                                  }}
                                  disabled={submitting}
                                  sx={{
                                    color: '#f5576c',
                                    '&.Mui-checked': { color: '#f5576c' }
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {role.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {role.description || 'Без описания'}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                      </FormGroup>
                      {errors.roleIds && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                          {errors.roleIds.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </CardContent>
            </Card>
          </Fade>

          <Fade in={mounted} timeout={1000}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
                startIcon={<CancelIcon />}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#667eea',
                    bgcolor: 'rgba(102, 126, 234, 0.08)'
                  }
                }}
              >
                Отмена
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)'
                  }
                }}
              >
                {submitting ? 'Создание...' : 'Создать пользователя'}
              </Button>
            </Box>
          </Fade>
        </form>
      )}
    </Box>
  );
}